import React from 'react';
import { spine } from '../spine-webgl';
import { mapSize } from '../constants/constants';
import "./WebGLContext.scss";
import cx from 'classnames';
import { delay } from '../services/util';
import doNotUseAccessibleStaticElementInteractions from "A11y/doNotUseAccessibleStaticElementInteractions.js";
import { GetCharacterConfiguration } from '../services/CharacterService';
import { ErrorModal, getErrorModalTypes } from './Reader/ErrorModal';
import { logSessionInfo, logSessionInfoBuffered } from '../services/logger';
import * as Sentry from '@sentry/browser';
import { FEATURE_FLAGS } from '../constants/featureFlags';

var animationId;
var mat4 = require('gl-matrix-mat4');

let FRAME_RATE = 30; // approximate frame rate. this won't be precise because we're still using requestAnimationFrame

const ENABLE_RENDERING = true;
let isMounted = false;
let processingFinished = false;

// used to track the background location for continuous background scrolling
let lastBackgroundX = 0;
let lastBackgroundY = 0;
let avatarHidden = false;
let processingSkeleton = {
  scale: new spine.Vector2(1, 1),
  pos: new spine.Vector2(mapSize.width / 2, mapSize.height / 2),
  spineContent: null,
  depth: 12,
};

let processingEvent = null;

function MyEventTarget() {
  var target = document.createTextNode(null);
  this.addEventListener = target.addEventListener.bind(target);
  this.removeEventListener = target.removeEventListener.bind(target);
  this.dispatchEvent = target.dispatchEvent.bind(target);
}
MyEventTarget.prototype = EventTarget.prototype;

function isiOS13ORLower(){
  if(!navigator.userAgent) return false
  if(!navigator.maxTouchPoints && navigator.maxTouchPoints <= 1) return false
  const isiPadMobile13OrLower = navigator.userAgent.match(/(iPad).*OS\s(12_?\d*|13_?\d*)/gi)
  const isiPadButDesktopVersionRequested = navigator.userAgent.match(/(macintosh.*applewebKit)/gi)
  return isiPadMobile13OrLower || isiPadButDesktopVersionRequested
}

class WebGLContext extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      clickablesToRender: [],
      zVal: '',
      background: null,
      className: null,
      initializationError: false,
    };
    // Spines to render is no longer a state because we do not need to rerender the canvas when this changes
    this.spinesToRender = [];
    this.transform = new spine.webgl.Matrix4();
    this.canvas = null;
    this.canvasRef = React.createRef();
    this.gl = null;
    this.shader = null;
    this.batcher = null;
    this.mvp = new spine.webgl.Matrix4();
    this.assetManager = null;
    this.skeletonRenderer = null;
    this.realPixelToWorldScale = 1;
    this.orthoBounds = {
      x: 0,
      y: 0,
      width: 2048,
      height: 1152,
    };
    this.drawImageShaderProgram = null;
    this.visemeArray = [];
    this.gestureArray = [];
    this.amiraPlayAnimations = false;
    this.hideAmira = false;
    this.resetAmira = false;
    processingEvent = isiOS13ORLower() ? new MyEventTarget() : new EventTarget();

    this.init = this.init.bind(this);
    this.initWithRetries = this.initWithRetries.bind(this);
    this.load = this.load.bind(this);

    // Any spine which will be used must be loaded here so that we can access it when we want it. Once we have better memory management, this will likely change
    this.loadSpineSkeleton = this.loadSpineSkeleton.bind(this);
    this.loadSkeletonData = this.loadSkeletonData.bind(this);
    this.createSpineInstance = this.createSpineInstance.bind(this);
    this.renderAnimation = this.renderAnimation.bind(this);
    this.resize = this.resize.bind(this);
    this.sendSpineRenderable = this.sendSpineRenderable.bind(this);
    this.removeSpineRenderable = this.removeSpineRenderable.bind(this);
    this.startLoadingAnimation = this.startLoadingAnimation.bind(this);
    this.removeLoadingAnimation = this.removeLoadingAnimation.bind(this);
    this.handleContextRestored = this.handleContextRestored.bind(this);
    this.handleContextLost = this.handleContextLost.bind(this);
    this.enabled = true;

    // TT-1378 render-path instrumentation. These make silent failure modes
    // observable (init/asset-load hang, "success but blank", hidden canvas,
    // context loss) which today leave no telemetry in S3 or Sentry.
    this._initStartTs = 0; // Date.now() when init lifecycle began
    this._spineLoadStartTs = 0; // Date.now() when spine asset load began
    this._initAttempts = 0; // number of init() attempts (initWithRetries runs up to 3)
    this._webglInitWatchdog = null; // fires if the spine asset load never settles
    this._firstFrameLogged = false; // one-shot guard for webgl_first_frame
    this._contextLostLogged = false; // one-shot guard per loss/restore cycle
    this._unmounted = false; // instance-level unmount guard for async callbacks
  }

  componentWillUnmount() {
    isMounted = false;
    this._unmounted = true; // gate async init/watchdog callbacks on this instance

    // Clear TT-1378 watchdog timer so it can't fire after unmount
    this.clearWebglWatchdogs();

    // Cancel any pending animation frames first
    if (animationId) {
      cancelAnimationFrame(animationId);
      animationId = null;
    }
    
    // Remove context event listeners
    if (this.canvasRef.current) {
      this.canvasRef.current.removeEventListener('webglcontextrestored', this.handleContextRestored);
      this.canvasRef.current.removeEventListener('webglcontextlost', this.handleContextLost);
    }
    
    // Remove window event listener
    window.removeEventListener('webgl-context-restored-complete', this.handleManagedContextRestored);
    
    processingSkeleton.spineContent = {
      scale: new spine.Vector2(1, 1),
      pos: new spine.Vector2(mapSize.width / 2, mapSize.height / 2),
      spineContent: null,
      depth: 12,
    };

    // Dispose of webgl releated data
    if (this.assetManager) {
      this.assetManager.removeAll();
    }
    if (this.shader) {
      this.shader.dispose();
    }
    if (this.batcher) {
      this.batcher.dispose();
    }
    if (this.gl && this.gl.canvas) {
      this.gl.canvas.width = 1;
      this.gl.canvas.height = 1;
    }
    if (this.gl && this.gl.getExtension('WEBGL_lose_context') && !this.gl.isContextLost()) {
      // Make sure we haven't lost context already
      this.gl.getExtension('WEBGL_lose_context').loseContext();
    }

    // Clear out webgl objects
    this.spinesToRender = [];
    this.canvas = null;
    this.gl = null;
    this.shader = null;
    this.batcher = null;
    this.assetManager = null;
    this.skeletonRenderer = null;
    this.transform = null;
    this.mvp = null;
  }

  componentDidMount() {
    isMounted = true;
    
    // Add context loss handlers
    if (this.canvasRef.current) {
      this.canvasRef.current.addEventListener('webglcontextlost', this.handleContextLost);
      this.canvasRef.current.addEventListener('webglcontextrestored', this.handleContextRestored);
    }
    
    // Listen for ManagedWebGLRenderingContext restoration complete event
    window.addEventListener('webgl-context-restored-complete', this.handleManagedContextRestored);

    // TT-1378: mark the start of the render lifecycle. Absence of a matching
    // webgl_init_result / webgl_first_frame for this session then means the
    // pipeline stalled before completing, which is otherwise invisible. These
    // fire at app boot (before login resolves a key), so they must be buffered
    // — see logSessionInfoBuffered / flushBufferedSessionLogs.
    this._initStartTs = Date.now();
    logSessionInfoBuffered('webgl_init_started', { ...this.memInfo() });

    this.initWithRetries().then((success) => {
      console.log('WebGLContext: initWithRetries completed with success:', success);
      if (this._unmounted) return; // unmounted mid-init: don't log/setState on a dead instance
      logSessionInfoBuffered('webgl_init_result', {
        success,
        attempts: this._initAttempts,
        elapsedMs: Date.now() - this._initStartTs,
        ...this.rendererInfo(),
      });
      if (!success) {
        this.setState({ initializationError: true });
        this.removeLoadingAnimation();
      }
      // On success, the positive webgl_first_frame event (logged when content is
      // actually drawn) is the render-health signal. There is deliberately no
      // time-based "no first frame" watchdog: the avatar is not queued until well
      // after login/activity start, so a boot-anchored timer measured the wrong
      // window and fired on the login screen. A blank canvas is instead diagnosed
      // by webgl_init_result{success:true} with no subsequent webgl_first_frame in
      // a session that otherwise progressed (transcript_phrase, scored_tutor, …).
    });
  }

  handleContextLost(e) {
    console.log('WebGL context lost');
    
    if (window.TEXTURE_DEBUG_METRICS) {
      window.TEXTURE_DEBUG_METRICS.contextLossCount++;
      window.TEXTURE_DEBUG_METRICS.addEvent('context-lost', { 
        reason: e?.statusMessage || 'unknown',
        activeSpines: this.spinesToRender?.length || 0
      });
    }
    
    if (isMounted) {
      this.logSpineContextLostAndRestoreError('LOST', {
        contextLostTimestamp: Date.now(),
        contextLostReason: e?.statusMessage || 'unknown',
        activeSpines: this.spinesToRender?.length || 0
      });

      // TT-1378: mirror the loss into the S3 session timeline (until now this
      // only went to Sentry, so it never correlated with the activity's other
      // client-side events). Guarded to one log per loss/restore cycle — reset
      // in handleContextRestored — so a flapping GPU still records each loss
      // without spamming a PUT on every event of a single loss.
      if (!this._contextLostLogged) {
        this._contextLostLogged = true;
        logSessionInfoBuffered('webgl_context_lost', {
          reason: e?.statusMessage || 'unknown',
          activeSpines: this.spinesToRender?.length || 0,
          msSinceInit: this._initStartTs ? Date.now() - this._initStartTs : null,
          ...this.memInfo(),
        });
      }
    }
    e?.preventDefault(); // Allow restoration
  }

  handleContextRestored() {
    console.log('WebGL context restored');

    // TT-1378: re-arm the one-shot so the next loss in a flap cycle is recorded.
    this._contextLostLogged = false;

    if (window.TEXTURE_DEBUG_METRICS) {
      window.TEXTURE_DEBUG_METRICS.contextRestoreCount++;
      window.TEXTURE_DEBUG_METRICS.addEvent('context-restored', {
        timestamp: Date.now()
      });
    }
    
    // Don't restart rendering here - wait for the ManagedWebGLRenderingContext
    // to finish restoring textures and fire 'webgl-context-restored-complete' event
    // The rendering loop will restart when we receive that event
  }
  
  handleManagedContextRestored = (event) => {
    // This fires AFTER ManagedWebGLRenderingContext has restored all textures/shaders
    console.log('Managed context restoration complete', event.detail);
    
    // Cancel any existing animation frames to prevent multiple loops
    if (animationId) {
      cancelAnimationFrame(animationId);
      animationId = null;
    }
    
    // Verify component is still mounted and context is valid
    if (!isMounted) {
      console.warn('Component unmounted, skipping rendering loop restart');
      return;
    }
    
    if (this.gl && !this.gl.isContextLost()) {
      // Restart the rendering loop now that all resources are restored
      animationId = requestAnimationFrame(this.renderAnimation);
      console.log('Restarting rendering loop');
    } else {
      console.error('WebGL context still lost after restoration');
      this.logSpineContextLostAndRestoreError('RESTORE_FAILED', {
        contextRestoredTimestamp: Date.now(),
        contextRestoreFailReason: 'Context still lost after restore event',
      });
      
      window.dispatchEvent(new CustomEvent('webgl-texture-restoration-errors', {
        detail: {
          errors: [{ error: 'Context still lost after restore' }],
          timestamp: Date.now()
        }
      }));
    }
  };

  stopAnimation(){
    if (animationId) {
      cancelAnimationFrame(animationId);
      animationId = null;
    }
    // TT-1378: a hidden/stopped canvas looks identical to a render failure from
    // the outside; log the cause so the two can be told apart in the timeline.
    // Only log the visible->hidden transition so repeated no-op calls don't each
    // cost an S3 PUT.
    const wasVisible = this.canvasRef.current && this.canvasRef.current.style.display !== 'none';
    if (this.canvasRef.current) this.canvasRef.current.style.display = 'none';
    if (wasVisible) {
      logSessionInfoBuffered('webgl_canvas_hidden', { source: 'stopAnimation', avatarHidden });
    }
  }

  hideAvatar(){
    avatarHidden = true;
    const wasVisible = this.canvasRef.current && this.canvasRef.current.style.display !== 'none';
    if (this.canvasRef.current) this.canvasRef.current.style.display = 'none';
    if (wasVisible) {
      logSessionInfoBuffered('webgl_canvas_hidden', { source: 'hideAvatar', avatarHidden });
    }
  }

  showAvatar(){
    avatarHidden = false;
    this.canvasRef.current.style.display = 'block';
  }

  setRenderError(error) {
    // Allow components to signal that rendering has failed after retries
    // This treats it like an initialization error so the app handles it consistently
    if (error && !this.state.initializationError) {
      this.setState({ initializationError: true });
    }
  }

  async initWithRetries(maxRetries = 3, retryDelay = 200) {
    let retryCount = 0;
    
    const attemptInit = async () => {
      const initSuccess = await this.init();
      if (initSuccess) {
        this.resize();
        return true;
      }
      
      retryCount++;
      if (retryCount < maxRetries) {
        console.log(`WebGL initialization attempt ${retryCount} failed, retrying...`);
        return new Promise(resolve => {
          setTimeout(async () => {
            resolve(await attemptInit());
          }, retryDelay);
        });
      } else {
        console.error(`WebGL initialization failed after ${maxRetries} attempts`);
        return false;
      }
    };
    
    return attemptInit();
  }

  async init() {
    this._initAttempts++; // initWithRetries may call init() up to 3 times
    try {
      this.canvas = document.getElementById('canvas');
      if (!this.canvas) {
        console.error('Canvas element not found');
        return false;
      }
      
      this.canvas.width = window.innerWidth;
      this.canvas.height = window.innerHeight;
      var config = { alpha: true };
      
      try {
        this.gl = this.canvas.getContext('webgl', config) || 
                  this.canvas.getContext('experimental-webgl', config);
      } catch (e) {
        console.error('WebGL context creation failed', e);
        this.gl = null;
      }
      
      if (!this.gl) {
        console.error('WebGL is unavailable.');
        // TT-1378: distinguish "no WebGL at all" (e.g. hardware acceleration
        // disabled / GPU blocklisted) from a later stall. This path already
        // ends in the static fallback via initWithRetries.
        logSessionInfoBuffered('webgl_context_created', { ok: false, attempt: this._initAttempts });
        return false;
      }

      // Create ManagedWebGLRenderingContext from canvas for proper context loss/restore handling
      this.managedContext = new spine.webgl.ManagedWebGLRenderingContext(this.canvas, config);

      // Create components sequentially with checks, passing the managed context
      try {
        this.shader = spine.webgl.Shader.newTwoColoredTextured(this.managedContext);
        this.batcher = new spine.webgl.PolygonBatcher(this.managedContext);
        this.skeletonRenderer = new spine.webgl.SkeletonRenderer(this.managedContext);
        this.assetManager = new spine.webgl.AssetManager(this.managedContext);
        
        // Verify critical components were created
        if (!this.shader || !this.batcher || !this.skeletonRenderer || !this.assetManager) {
          throw new Error('Failed to initialize one or more WebGL components');
        }
      } catch (e) {
        console.error('Error during WebGL component initialization', e);
        // TT-1378: a shader/batcher/renderer/assetManager construction failure
        // (plausible on a blocklisted GPU — the hardware-acceleration-off case)
        // is otherwise indistinguishable from any other unlogged stage. Cover
        // it so every init() failure branch leaves a marker.
        logSessionInfoBuffered('webgl_context_created', {
          ok: false,
          stage: 'component_init',
          attempt: this._initAttempts,
          error: e && e.message ? e.message : String(e),
        });
        return false;
      }

      // TT-1378: capture the live GPU context. The renderer string reveals a
      // software fallback (SwiftShader) vs a real GPU, which directly tests the
      // "enable hardware acceleration" theory.
      logSessionInfoBuffered('webgl_context_created', { ok: true, attempt: this._initAttempts, ...this.rendererInfo() });

      const characterConfig = GetCharacterConfiguration();
      const skeletonPath = characterConfig.cdnSkeletonURL;
      const atlasPath = characterConfig.cdnAtlasURL;
      const texturePaths = characterConfig.cdnTextureURLs;

      // Load the character's skeleton and wait for it to complete
      this._spineLoadStartTs = Date.now();
      logSessionInfoBuffered('webgl_spine_load_started', {
        rig: characterConfig.rig,
        skin: characterConfig.skin,
        skeletonURL: skeletonPath,
        atlasURL: atlasPath,
        textureCount: Array.isArray(texturePaths) ? texturePaths.length : 0,
      });
      // Watchdog: the spine AssetManager loaders only surface a *failure* via
      // their error callback (WEBGL_SPINE_ASSET_LOAD_FAILURE). A stalled fetch
      // that neither resolves nor rejects hangs init() forever. This timer makes
      // that stall observable. It is telemetry-only: it does NOT flip
      // initializationError, because a slow-but-successful load (a cold-cache 4GB
      // Chromebook loading the 20-texture Amira2 rig can exceed the threshold)
      // would otherwise be stuck on the static fallback for the whole session
      // (Coach treats the flag as sticky). Driving the fallback from a genuine
      // stall is tracked separately.
      this.armWebglInitWatchdog(characterConfig);
      try {
        const loadPromise = this.loadSpineSkeleton(skeletonPath, atlasPath, texturePaths);
        await loadPromise;
        logSessionInfoBuffered('webgl_spine_load_completed', {
          elapsedMs: Date.now() - this._spineLoadStartTs,
          // loadSpineSkeleton returns undefined (resolves immediately) when
          // assetManager is null, so surface whether a load actually happened
          // rather than reporting a ~0ms "completed" for a no-op.
          loadInvoked: !!loadPromise,
          isLoadingComplete: this.assetManager && this.assetManager.isLoadingComplete
            ? this.assetManager.isLoadingComplete()
            : null,
        });
      } catch (error) {
        console.error('Failed to load spine skeleton:', error);
        return false;
      } finally {
        // Clear on every exit path — including a *synchronous* throw from
        // loadSpineSkeleton (e.g. a non-iterable cdnTextureURLs, which the
        // Array.isArray guard above hedges against). Clearing only around the
        // await would leak the timer on such a throw and fire a false
        // webgl_init_timeout ~12s later, misdiagnosing a synchronous config/GPU
        // crash as a stalled asset load — the exact wrong signal for this bug.
        this.clearWebglInitWatchdog();
      }

      this.timeSinceStart = 0;
      this.lastFrameTime = 0;

      animationId = requestAnimationFrame(this.load);
      return true;
    } catch (e) {
      console.error('Unhandled exception in init()', e);
      return false;
    }
  }

  loadSpineSkeleton(skeletonPath, atlasPath, texturePaths) {
    if (!this.assetManager) {
      console.error('Asset manager is null, cannot load spine skeleton');
      return;
    }

    console.log("WebGLContext: Loading remote skeleton from:", skeletonPath);
    console.log("WebGLContext: Loading remote atlas from:", atlasPath);
    console.log("WebGLContext: Loading remote textures:", texturePaths);
    
    const requestedAssets = [skeletonPath, atlasPath, ...texturePaths];

    const assetsToRemove = [];
    for (const key in this.assetManager.assets) {
      if (!requestedAssets.includes(key)) {
        assetsToRemove.push(key);
      }
    }

    assetsToRemove.forEach(path => {
      console.log(`WebGLContext: Removing unused asset: ${path}`);
      this.assetManager.remove(path);
    });

    const loadPromises = [];

    // Load skeleton if not already loaded
    if (!this.assetManager.get(skeletonPath)) {
      const skeletonPromise = new Promise((resolve, reject) => {
        this.assetManager.loadBinary(
          skeletonPath,
          () => {
            console.log(`WebGLContext: Successfully loaded skeleton: ${skeletonPath}`);
            resolve();
          },
          (path, error) => {
            console.error(`WebGLContext: Failed to load skeleton. Path: ${path}, Error: ${error}`);
            logSessionInfo("WEBGL_SPINE_ASSET_LOAD_FAILURE", {
              assetType: "skeleton",
              path: path,
              error: error.toString(),
              userAgent: navigator.userAgent,
            });
            reject(error);
          }
        );
      });
      loadPromises.push(skeletonPromise);
    }

    // Load atlas if not already loaded
    if (!this.assetManager.get(atlasPath)) {
      const atlasPromise = new Promise((resolve, reject) => {
        this.assetManager.loadTextureAtlas(
          atlasPath,
          () => {
            console.log(`WebGLContext: Successfully loaded atlas: ${atlasPath}`);
            resolve();
          },
          (path, error) => {
            console.error(`WebGLContext: Failed to load atlas. Path: ${path}, Error: ${error}`);
            logSessionInfo("WEBGL_SPINE_ASSET_LOAD_FAILURE", {
              assetType: "atlas",
              path: path,
              error: error.toString(),
              userAgent: navigator.userAgent,
            });
            reject(error);
          }
        );
      });
      loadPromises.push(atlasPromise);
    }

    // Load each texture if not already loaded
    texturePaths.forEach(texturePath => {
      if (!this.assetManager.get(texturePath)) {
        const texturePromise = new Promise((resolve, reject) => {
          this.assetManager.loadTexture(
            texturePath,
            () => {
              console.log(`WebGLContext: Successfully loaded texture: ${texturePath}`);
              resolve();
            },
            (path, error) => {
              console.error(`WebGLContext: Failed to load texture. Path: ${path}, Error: ${error}`);
              logSessionInfo("WEBGL_SPINE_ASSET_LOAD_FAILURE", {
                assetType: "texture",
                path: path,
                error: error.toString(),
                userAgent: navigator.userAgent,
              });
              reject(error);
            }
          );
        });
        loadPromises.push(texturePromise);
      }
    });

    // Return a promise that resolves when all assets are loaded
    return Promise.all(loadPromises).then(() => {
      console.log("WebGLContext: All spine assets loaded successfully!");
      return true;
    }).catch(error => {
      console.error("WebGLContext: Failed to load some spine assets:", error);
      logSessionInfo("WEBGL_SPINE_ASSETS_LOAD_FAILURE", {
        error: error.toString(),
        userAgent: navigator.userAgent,
        characterConfig: GetCharacterConfiguration()
      });
      throw error;
    });
  }

  load() {
    // If component is unmounting or context is lost, don't continue
    if (!isMounted || !this.gl) {
      if (animationId) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
      return;
    }

    // Defensive check for asset manager
    if (!this.assetManager) {
      console.error('AssetManager is null in load() call');
      
      // Try to recover by reinitializing if possible
      try {
        if (this.gl && !this.gl.isContextLost()) {
          console.log("Attempting to reinitialize assetManager");
          this.assetManager = new spine.webgl.AssetManager(this.gl);
          
          // Reload necessary assets
          const characterConfig = GetCharacterConfiguration();
          const skeletonPath = characterConfig.cdnSkeletonURL;
          const atlasPath = characterConfig.cdnAtlasURL;
          const texturePaths = characterConfig.cdnTextureURLs;

          // Load the character's skeleton
          this.loadSpineSkeleton(skeletonPath, atlasPath, texturePaths);
        }
      } catch (e) {
        console.error('Failed to recover from null assetManager', e);
      }
      
      // Schedule another attempt with backoff
      setTimeout(() => {
        if (isMounted) {
          animationId = requestAnimationFrame(this.load);
        }
      }, 500);
      
      return;
    }

    // Safe to proceed with original logic
    try {
      if (this.assetManager.isLoadingComplete()) {
        console.log('Finished loading assets');
        console.log("calling load()");
        animationId = requestAnimationFrame(this.renderAnimation);
      } else {
        animationId = requestAnimationFrame(this.load);
      }
    } catch (e) {
      console.error('Error in load() execution', e);
      // Attempt recovery
      setTimeout(() => {
        if (isMounted) {
          animationId = requestAnimationFrame(this.load);
        }
      }, 300);
    }
  }

  loadSkeletonData(skeletonPath, atlasPath, texturePaths, callback) {
    if (!this.assetManager) {
      console.error('Asset manager is null, cannot load skeleton data');
      return null;
    }
    
    // Load the texture atlas from the AssetManager.
    var atlas = this.assetManager.get(atlasPath);

    const self = this;

    const onAtlasLoaded = (foundAtlas) => {
      var atlasLoader = new spine.AtlasAttachmentLoader(foundAtlas);

      var skeletonBinary = new spine.SkeletonBinary(atlasLoader);

      skeletonBinary.scale = 1;
      const skeletonAsset = self.assetManager.get(skeletonPath);
      callback(skeletonBinary.readSkeletonData(skeletonAsset));
    }

    if (!atlas) {
      this.loadSpineSkeleton(skeletonPath, atlasPath, texturePaths)
        .then(() => {
          const loadedAtlas = self.assetManager.get(atlasPath);
          onAtlasLoaded(loadedAtlas);
        })
    } else {
      onAtlasLoaded(atlas);
    }
  }

  createSpineInstance(skeletonData, initialAnimation, name, skinName) {
    var skeleton = new spine.Skeleton(skeletonData);

    if (skinName) {
      skeleton.setSkinByName(skinName);
    }

    // skeleton.setSkinByName("Outline");
    // var bounds = this.calculateSetupPoseBounds(skeleton);
    skeleton.setToSetupPose();
    skeleton.updateWorldTransform();
    var localBounds = this.calculateLocalBounds(skeleton);

    // Create an AnimationState, and set the initial animation in looping mode.
    var animationStateData = new spine.AnimationStateData(skeleton.data);
    if (!animationStateData) {
      throw new Error('animationStateData is false.');
    }
    var animationState = new spine.AnimationState(animationStateData);
    if (!animationState) {
      throw new Error('animationState is false.');
    }

    if (initialAnimation) {
      if (name === 'amira') {
        animationState.setAnimation(0, initialAnimation, true);
        animationState.setAnimation(1, 'gestures/Idle', true);
      } else {
        animationState.setAnimation(0, initialAnimation, true); // Setup default anim
      }
    }

    let premultipliedAlpha = false;
    if (name === 'amira') {
      premultipliedAlpha = true;
    }

    // Pack everything up and return to caller.
    return {
      skeleton: skeleton,
      state: animationState,
      x: 0,
      y: 0,
      premultipliedAlpha: premultipliedAlpha,
      localBounds: localBounds,
    };
  }

  
  resize() {
    this.canvas.width = 2048;
    this.canvas.height = 1152;

    // Normalized background dimensions:
    // 2048x1152 is the resolution used for campaign maps, so it makes sense to use as
    // our world size as well. We could have custom world sizes depending on the screen
    // shown, but most other content will probably be placed by pixel coordinate or screen
    // percent so it seems simpler to keep it constant.
    var backgroundWidth = 2048;
    var backgroundHeight = 1152;
    var backgroundAspect = backgroundWidth / backgroundHeight;

    // Bounded ortho view
    this.orthoBounds.width = backgroundWidth;
    this.orthoBounds.height = backgroundHeight;
    var screenAspect = 2048 / 1152;
    if (backgroundAspect > screenAspect) {
      // Background image will be cropped with gutters on left and right
      this.orthoBounds.width = this.orthoBounds.height * screenAspect;
    } else {
      // Gutters on top and bottom
      this.orthoBounds.height = this.orthoBounds.width / screenAspect;
    }
    this.orthoBounds.x = (backgroundWidth - this.orthoBounds.width) / 2;
    this.orthoBounds.y = (backgroundHeight - this.orthoBounds.height) / 2;
    this.realPixelToWorldScale = this.orthoBounds.width / this.canvas.width;

    this.mvp.ortho2d(
      this.orthoBounds.x,
      this.orthoBounds.y,
      this.orthoBounds.width,
      this.orthoBounds.height
    );
    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
  }

  // TODO: pure function
  calculateLocalBounds(skeleton) {
    var offset = new spine.Vector2();
    var size = new spine.Vector2();
    skeleton.getBounds(offset, size, []);

    return { offset: offset, size: size };
  }

  drawBackground(secondsElapsed) {
    let { translateXandY, texture } = this.state.background;
    const backgroundGLWidth = 9; //width of the background in webgl units. In the future we should convert everything to either world or pixles

    let numImages = 3; //Math.ceil(this.canvas.width / backgroundImage.width) + 1;
    let { newX, newY } = translateXandY(secondsElapsed);

    // draw 3 images at appropriate offsets, one behind, one infront, and one current.
    //  Start at -1 so that we have 1 behind us in case we move backwards off the edge of the curent image
    for (var i = -1; i < numImages - 1; i++) {
      this.drawImage(texture, i * backgroundGLWidth - newX, newY);
    }
  }

  renderAnimation() {
    // If component is unmounting or context is lost, don't continue
    if (!isMounted || !this.gl || this.gl.isContextLost()) {
      if (animationId) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
      return;
    }

    if(this.canvasRef.current && this.canvasRef.current.style.display === 'none' && !avatarHidden){
      this.canvasRef.current.style.display = 'block'
    }

    if (isMounted && this.enabled) {
      var now = Date.now() / 1000;
      var delta = 0;
      if (!(this.lastFrameTime === 0)) {
        delta = now - this.lastFrameTime;
        // manually enforce a specific frame rate, if it is slower than the device's refresh rate
        if (delta * 1000 < 1000 / FRAME_RATE) {
          animationId = requestAnimationFrame(this.renderAnimation);
          return;
        }
      }
      this.lastFrameTime = now;
      this.timeSinceStart += delta;

      this.gl.clearColor(0, 0, 0, 0);
      this.gl.clear(this.gl.COLOR_BUFFER_BIT);

      if (this.state.background) {
        this.drawBackground(delta);
      }

      for (let i = 0; i < this.spinesToRender.length; i++) {
        var renderable = this.spinesToRender[i].spineContent;
        let scale = this.spinesToRender[i].scale;
        let pos = this.spinesToRender[i].pos;

        var skeleton = renderable.skeleton;
        var state = renderable.state;
        var premultipliedAlpha = renderable.premultipliedAlpha;

        if (this.spinesToRender[i].name && this.spinesToRender[i].name === 'amira') {
          if (this.resetAmira) {
            this.timeSinceStart = 0;
            this.lastFrameTime = 0;
            state.setAnimation(0, 'visemes/viseme_sil', true);
            state.setAnimation(1, 'gestures/Idle', true);
            this.resetAmira = false;
          }

          // If there are visemes to play, play them at the correct time
          if (this.visemeArray.length !== 0 && this.amiraPlayAnimations) {
            let currViseme = this.visemeArray[0];
            if (currViseme.time <= this.timeSinceStart * 1000) {
              this.visemeArray.shift();
              state.setAnimation(0, 'visemes/viseme_' + currViseme.value, false);
            }
          }

          // If there are gestures to play, play them at the correct time
          if (this.gestureArray.length !== 0 && this.amiraPlayAnimations) {
            let currGesture = this.gestureArray[0];
            if (currGesture.time <= this.timeSinceStart * 1000) {
              this.gestureArray.shift();
              //console.log("Gesture: " + currGesture.value + " - Gesture Time: " + currGesture.time + " - Actual Time: " + this.timeSinceStart * 1000);
              if (currGesture.value !== 'endPhrase') {
                state.setAnimation(1, currGesture.value, false);
              }
            }
          }

          if (
            this.visemeArray.length === 0 &&
            this.gestureArray.length === 0 &&
            this.amiraPlayAnimations
          ) {
            state.setAnimation(0, 'visemes/viseme_sil', true);
            state.setAnimation(1, 'gestures/Idle', true);
            this.amiraPlayAnimations = false;
          }
        }

        // Apply the animation state based on the delta time in seconds.
        state.update(delta);
        state.apply(skeleton);
        skeleton.updateWorldTransform();

        // Bind the shader and set the texture and model-view-projection matrix.
        this.shader.bind();
        this.shader.setUniformi(spine.webgl.Shader.SAMPLER, 0);

        this.transform.identity();
        this.transform.values[spine.webgl.M00] = scale.x;
        this.transform.values[spine.webgl.M11] = scale.y;
        this.transform.translate(pos.x, pos.y, 0);
        this.transform.multiplyLeft(this.mvp);
        this.shader.setUniform4x4f(spine.webgl.Shader.MVP_MATRIX, this.transform.values); //TODO: call this from the draw function

        // Start the batch and tell the SkeletonRenderer to render the active skeleton.
        if (ENABLE_RENDERING) {
          this.batcher.begin(this.shader);
          this.skeletonRenderer.premultipliedAlpha = premultipliedAlpha;
          this.skeletonRenderer.draw(this.batcher, skeleton);
          this.batcher.end();
        }

        this.shader.unbind();
      }

      // Reschedule the loop BEFORE any telemetry so a fault in logging can never
      // skip the next frame. logSessionInfoBuffered is now guaranteed not to
      // throw, but keeping the reschedule first is free defense-in-depth for the
      // one path where a throw would manufacture the white screen (TT-1378).
      animationId = requestAnimationFrame(this.renderAnimation);

      // TT-1378: one-shot confirmation that the loop actually drew content, not
      // just cleared to transparent. Logged once at least one spine was drawn
      // (spinesToRender is populated by Coach after the loop starts), so a blank
      // cleared canvas — visually identical to the white-screen symptom — does
      // not masquerade as a healthy first frame. `spineNames` disambiguates the
      // loading spinner (processingSkeleton, unnamed) from the actual avatar
      // ('amira'): a first frame with only the spinner is NOT the avatar
      // rendering, which is the half of CR-10713 being reported.
      if (!this._firstFrameLogged && this.spinesToRender.length > 0) {
        this._firstFrameLogged = true;
        logSessionInfoBuffered('webgl_first_frame', {
          msSinceInit: this._initStartTs ? Date.now() - this._initStartTs : null,
          spineCount: this.spinesToRender.length,
          spineNames: this.spinesToRender.map(s => (s && s.name) || null),
          hasAmira: this.spinesToRender.some(s => s && s.name === 'amira'),
          canvasDisplay: this.canvasRef.current ? this.canvasRef.current.style.display : null,
        });
      }
    }
  }

  sendSpineRenderable(spineRenderable) {
    this.spinesToRender = [...this.spinesToRender, spineRenderable];
    this.spinesToRender.sort(function (a, b) {
      let depthA = a.depth ? a.depth : 0;
      let depthB = b.depth ? b.depth : 0;
      return depthA - depthB;
    });
  }

  removeSpineRenderable(spineRenderable) {
    for (let i = 0; i < this.spinesToRender.length; i++) {
      if (this.spinesToRender[i] === spineRenderable) {
        this.spinesToRender.splice(i, 1);
      }
    }
  }

  startLoadingAnimation() {
    this.sendSpineRenderable(processingSkeleton);
  }

  async removeLoadingAnimation() {
    processingFinished = true;
    await new Promise((resolve, reject) => {
      processingEvent.addEventListener('finished', function resolveProcessingPromise() {
        processingEvent.removeEventListener('finished', resolveProcessingPromise);
        resolve();
      });
    });
  }

  // originally from https://developer.mozilla.org/en-US/docs/Web/API/WebGL_API/Tutorial/Adding_2D_content_to_a_WebGL_context
  // creates a shader program to use the image texture to fill the image box
  initDrawImageShaderProgram() {
    // Vertex shader program
    const vsSource = `
      attribute vec4 aVertexPosition;
      attribute vec2 aTextureCoord;

      uniform mat4 uModelViewMatrix;
      uniform mat4 uProjectionMatrix;

      varying highp vec2 vTextureCoord;

      void main(void) {
        gl_Position = uProjectionMatrix * uModelViewMatrix * aVertexPosition;
        vTextureCoord = aTextureCoord;
      }
    `;

    const fsSource = `
      varying highp vec2 vTextureCoord;

      uniform sampler2D uSampler;

      void main(void) {
        gl_FragColor = texture2D(uSampler, vTextureCoord);
      }
    `;

    const vertexShader = this.loadShader(this.gl.VERTEX_SHADER, vsSource);
    const fragmentShader = this.loadShader(this.gl.FRAGMENT_SHADER, fsSource);

    // Create the shader program

    const shaderProgram = this.gl.createProgram();
    this.gl.attachShader(shaderProgram, vertexShader);
    this.gl.attachShader(shaderProgram, fragmentShader);
    this.gl.linkProgram(shaderProgram);

    // If creating the shader program failed, alert

    if (!this.gl.getProgramParameter(shaderProgram, this.gl.LINK_STATUS)) {
      alert('Unable to initialize the shader program: ' + this.gl.getProgramInfoLog(shaderProgram));
      return null;
    }

    this.drawImageShaderProgram = shaderProgram;
  }

  // originally from https://developer.mozilla.org/en-US/docs/Web/API/WebGL_API/Tutorial/Adding_2D_content_to_a_WebGL_context
  // creates a shader of the given type, uploads the source and
  // compiles it.
  //
  loadShader(type, source) {
    const shader = this.gl.createShader(type);

    // Send the source to the shader object

    this.gl.shaderSource(shader, source);

    // Compile the shader program

    this.gl.compileShader(shader);

    // See if it compiled successfully

    if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
      alert('An error occurred compiling the shaders: ' + this.gl.getShaderInfoLog(shader));
      this.gl.deleteShader(shader);
      return null;
    }

    return shader;
  }

  initDrawImageBuffers() {
    // Create a buffer for the square's positions.

    const positionBuffer = this.gl.createBuffer();

    // Select the positionBuffer as the one to apply buffer
    // operations to from here out.

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, positionBuffer);

    // Now create an array of positions for the square.

    const positions = [-4.5, 3.5, 4.5, 3.5, -4.5, -3.5, 4.5, -3.5];

    // Now pass the list of positions into WebGL to build the
    // shape. We do this by creating a Float32Array from the
    // JavaScript array, then use it to fill the current buffer.

    this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(positions), this.gl.STATIC_DRAW);

    // Create a buffer for texture coords
    var texcoordBuffer = this.gl.createBuffer();
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, texcoordBuffer);

    // Put texcoords in the buffer
    // these are the four corners of the rectangular image. They need to be in the right order to produce the rectange we would expect
    var texcoords = [
      //other orders:  1,2,3,4,(mirror image)  1,3,2,4,(sideways)  1,4,2,3,(sideways and warped), 2,1,3,4,(warped)
      -0,
      -0, // 2
      1,
      -0, // 1
      -0,
      1, // 4
      1,
      1, // 3
    ];
    this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(texcoords), this.gl.STATIC_DRAW);

    return {
      position: positionBuffer,
      //color: colorBuffer, //this would be used to color the rectangle rather than using our image to fill it
      textureCoord: texcoordBuffer,
    };
  }

  drawScene(programInfo, buffers, texture, xtranslation, ytranslation) {
    this.gl.depthFunc(this.gl.LEQUAL); // Near things obscure far things

    // Create a perspective matrix, a special matrix that is
    // used to simulate the distortion of perspective in a camera.
    // Our field of view is 45 degrees, with a width/height
    // ratio that matches the display size of the canvas
    // and we only want to see objects between 0.1 units
    // and 100 units away from the camera.

    const fieldOfView = (45 * Math.PI) / 180; // in radians
    const aspect = this.gl.canvas.clientWidth / this.gl.canvas.clientHeight;
    const zNear = 0.1;
    const zFar = 100.0;
    const projectionMatrix = mat4.create();

    // note: glmatrix.js always has the first argument
    // as the destination to receive the result.
    mat4.perspective(projectionMatrix, fieldOfView, aspect, zNear, zFar);

    // Set the drawing position to the "identity" point, which is
    // the center of the scene.
    const modelViewMatrix = mat4.create();

    // Now move the drawing position a bit to where we want to
    // start drawing the square.

    mat4.translate(
      modelViewMatrix, // destination matrix
      modelViewMatrix, // matrix to translate
      [xtranslation, ytranslation * -1 + 1, -6.0]
    ); // amount to translate

    // Tell WebGL how to pull out the positions from the position
    // buffer into the vertexPosition attribute.
    {
      const numComponents = 2; // pull out 2 values per iteration
      const type = this.gl.FLOAT; // the data in the buffer is 32bit floats
      const normalize = false; // don't normalize
      const stride = 0; // how many bytes to get from one set of values to the next
      // 0 = use type and numComponents above
      const offset = 0; // how many bytes inside the buffer to start from
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, buffers.position);
      this.gl.vertexAttribPointer(
        programInfo.attribLocations.vertexPosition,
        numComponents,
        type,
        normalize,
        stride,
        offset
      );
      this.gl.enableVertexAttribArray(programInfo.attribLocations.vertexPosition);
    }

    // Tell WebGL how to pull out the colors from the color buffer
    // into the vertexColor attribute.

    // tell webgl how to pull out the texture coordinates from buffer
    {
      const num = 2; // every coordinate composed of 2 values
      const type = this.gl.FLOAT; // the data in the buffer is 32 bit float
      const normalize = false; // don't normalize
      const stride = 0; // how many bytes to get from one set to the next
      const offset = 0; // how many bytes inside the buffer to start from
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, buffers.textureCoord);
      this.gl.vertexAttribPointer(
        programInfo.attribLocations.textureCoord,
        num,
        type,
        normalize,
        stride,
        offset
      );
      this.gl.enableVertexAttribArray(programInfo.attribLocations.textureCoord);
    }

    // Tell WebGL to use our program when drawing

    this.gl.useProgram(programInfo.program);

    // Set the shader uniforms

    this.gl.uniformMatrix4fv(
      programInfo.uniformLocations.projectionMatrix,
      false,
      projectionMatrix
    );
    this.gl.uniformMatrix4fv(programInfo.uniformLocations.modelViewMatrix, false, modelViewMatrix);

    // Tell WebGL we want to affect texture unit 0
    this.gl.activeTexture(this.gl.TEXTURE0);

    // Bind the texture to texture unit 0
    this.gl.bindTexture(this.gl.TEXTURE_2D, texture);

    // Tell the shader we bound the texture to texture unit 0
    this.gl.uniform1i(programInfo.uniformLocations.uSampler, 0);

    {
      const offset = 0;
      const vertexCount = 4;
      this.gl.drawArrays(this.gl.TRIANGLE_STRIP, offset, vertexCount);
    }
  }

  // adapted in part from https://developer.mozilla.org/en-US/docs/Web/API/WebGL_API/Tutorial/Using_textures_in_WebGL
  drawImage(texture, x, y) {
    //console.log("in drawImage. texture: ", texture, ", x: ", x, ", y: ", y);
    //let texture = this.loadImageAndCreateTextureInfo(url);
    //console.log("texture: ", texture);

    if (!this.drawImageShaderProgram) {
      this.initDrawImageShaderProgram();
    }
    const shaderProgram = this.drawImageShaderProgram;
    const buffers = this.initDrawImageBuffers();

    const programInfo = {
      program: shaderProgram,
      attribLocations: {
        vertexPosition: this.gl.getAttribLocation(shaderProgram, 'aVertexPosition'),
        textureCoord: this.gl.getAttribLocation(shaderProgram, 'aTextureCoord'),
      },
      uniformLocations: {
        projectionMatrix: this.gl.getUniformLocation(shaderProgram, 'uProjectionMatrix'),
        modelViewMatrix: this.gl.getUniformLocation(shaderProgram, 'uModelViewMatrix'),
        uSampler: this.gl.getUniformLocation(shaderProgram, 'uSampler'),
      },
    };

    this.drawScene(programInfo, buffers, texture, x, y);

    //vertices array. Format per line: x,y,r,g,b,a,u,v,r2,g2,b2,a2
  }

  // --- TT-1378 render-path instrumentation helpers ---

  /**
   * Snapshot the live WebGL context for diagnostics. The unmasked renderer
   * string distinguishes a real GPU from a software fallback (SwiftShader),
   * which is the signal for whether hardware acceleration is effectively off.
   * All parameter reads are guarded so a driver quirk can never break init.
   */
  rendererInfo() {
    try {
      const gl = this.gl;
      if (!gl) return { glAvailable: false };
      const dbg = gl.getExtension('WEBGL_debug_renderer_info');
      return {
        glAvailable: true,
        isContextLost: gl.isContextLost(),
        renderer: dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
        vendor: dbg ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR),
        maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
        attributes: gl.getContextAttributes ? gl.getContextAttributes() : null,
      };
    } catch (e) {
      return { glAvailable: !!this.gl, rendererInfoError: e && e.message ? e.message : String(e) };
    }
  }

  /**
   * Compact memory/device snapshot for the CR-10679 memory-pressure angle.
   * performance.memory is Chrome-only and best-effort.
   */
  memInfo() {
    const mem = (typeof performance !== 'undefined' && performance.memory) ? performance.memory : null;
    return {
      deviceMemory: (typeof navigator !== 'undefined' && navigator.deviceMemory) || null,
      hardwareConcurrency: (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || null,
      jsHeap: mem ? {
        used: mem.usedJSHeapSize,
        total: mem.totalJSHeapSize,
        limit: mem.jsHeapSizeLimit,
      } : null,
    };
  }

  /**
   * Arm the init/asset-load hang watchdog. If the spine asset load has not
   * settled within WEBGL_INIT_WATCHDOG_MS, log the stall. Telemetry-only by
   * design: it deliberately does NOT flip initializationError, because a
   * slow-but-successful load would then be stuck on the static fallback for the
   * whole session (Coach treats the flag as sticky). Making a genuine stall
   * drive the fallback — without penalizing slow-but-healthy loads — is tracked
   * separately.
   */
  armWebglInitWatchdog(characterConfig) {
    const WEBGL_INIT_WATCHDOG_MS = 12000;
    this.clearWebglInitWatchdog();
    this._webglInitWatchdog = setTimeout(() => {
      this._webglInitWatchdog = null;
      if (this._unmounted) return;
      logSessionInfoBuffered('webgl_init_timeout', {
        stage: 'spine_asset_load',
        elapsedMs: this._spineLoadStartTs ? Date.now() - this._spineLoadStartTs : WEBGL_INIT_WATCHDOG_MS,
        rig: characterConfig ? characterConfig.rig : null,
        loadedAssets: this.assetManager ? Object.keys(this.assetManager.assets || {}) : [],
        isLoadingComplete: this.assetManager && this.assetManager.isLoadingComplete
          ? this.assetManager.isLoadingComplete()
          : null,
        ...this.rendererInfo(),
        ...this.memInfo(),
      });
    }, WEBGL_INIT_WATCHDOG_MS);
  }

  clearWebglInitWatchdog() {
    if (this._webglInitWatchdog) {
      clearTimeout(this._webglInitWatchdog);
      this._webglInitWatchdog = null;
    }
  }

  clearWebglWatchdogs() {
    this.clearWebglInitWatchdog();
  }

  logSpineContextLostAndRestoreError(event='LOST', extraData) {
    if (FEATURE_FLAGS.ENABLE_COMMON_SENTRY_ERROR_LOGGING) {
      Sentry.captureMessage(`SPINE_CONTEXT_${event}`, {
        level: 'warning',
        tags: {
          component: 'WebGLContext',
        },
        extra: extraData,
      });
    }
  }

  // creates a texture info { width: w, height: h, texture: tex }

  render() {    
    // This is the object that is passed down to all children (every component except speech) of WebGLContext
    const propsToPassDown = {
      init: this.init,
      resize: this.resize,
      stopAnimation: this.stopAnimation,
      loadSkeletonData: this.loadSkeletonData,
      createSpineInstance: (skeletonData, initialAnimation, name, skinName) =>
        this.createSpineInstance(skeletonData, initialAnimation, name, skinName),
      sendSpineRenderable: (spineRenderable) => this.sendSpineRenderable(spineRenderable),
      removeSpineRenderable: (spineRenderable) => this.removeSpineRenderable(spineRenderable),
      moveWebglLayer: (val) => this.setState({ zVal: val }),
      startLoadingAnimation: () => this.startLoadingAnimation(),
      removeLoadingAnimation: () => this.removeLoadingAnimation(),
      sendAmiraVisemeArray: (newVisemeArray) => {
        this.visemeArray = newVisemeArray;
      },
      sendAmiraGestureArray: (newGestureArray) => {
        this.gestureArray = newGestureArray;
      },
      setAmiraPlayAnimations: (playAnimations) => {
        this.amiraPlayAnimations = playAnimations;
      },
      resetAmiraAnimations: () => {
        this.resetAmira = true;
      },
      setClassName: (className) => this.setState({ className }),
      isEnabled: this.getIsEnabled,
      context: this.gl,
      canvasRef: this.canvasRef,
      drawImage: this.drawImage.bind(this),
      resize: this.resize,
      hideAvatar: this.hideAvatar,
      showAvatar: this.showAvatar,
      setRenderError: this.setRenderError.bind(this),
      webglError: this.state.initializationError,
    };

    let canvasDivClasses = 'webgl-div';
    if (this.state.className) {
      canvasDivClasses += ' ' + this.state.className;
    }

    const canvasClass = cx({
      webGLCanvas: true,
      [this.state.zVal]: !!this.state.zVal,
      hidden: this.hideAmira
    })

    // zIndex was originally 1 - I set it to 12 to show it during the grad experience
    return (
      <div className={canvasDivClasses}
           {...doNotUseAccessibleStaticElementInteractions({onDragStart: e => e.preventDefault()})}>
        <canvas id="canvas" ref={this.canvasRef} className={canvasClass} />
        {this.props.children(propsToPassDown)}

        {this.state.clickablesToRender &&
          this.state.clickablesToRender.map((div, index) => {
            return div.renderable;
          })}
      </div>
    );
  }
}

export default WebGLContext;
